"use client";
import { jsx } from "react/jsx-runtime";
import { DatePicker } from "@pcln/horizon";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FooterLeftNode from "./FooterLeftNode.js";
import { getPricingData } from "./getPricingData/getPricingData.js";
import { getDisplayMonths, pricePromise, processRecords, shouldCallCalendarApi } from "./helper/helper.js";
const getLowestPrice = (priceMap)=>Math.min(...Object.keys(priceMap).map((k)=>priceMap[k].price));
const DAY_MAP_VARIANT = 'dayHeatMap';
const PRICE_MONTHS = 2;
function HorizonFlyDatePicker({ flightDetails, startDate, variantName, getGraphURL, apolloClientInfo, selected, mode = 'range', numberOfMonths = 2, defaultMonth, startMonth, endMonth, title, label, handleCalendarChange, ...restProps }) {
    const executedMonths = useRef({});
    const [dayMap, setDayMap] = useState({
        variant: DAY_MAP_VARIANT
    });
    const getAirPricedCalendarData = useCallback(async ({ displayMonths, departDate, clearDayMap })=>{
        if (!shouldCallCalendarApi({
            flightDetails
        })) return;
        if (clearDayMap) executedMonths.current = {};
        const pricingPromise = getPricingData({
            displayMonths,
            flightDetails,
            pricePromise: pricePromise,
            variantName,
            executedMonths,
            departDate,
            getGraphURL,
            apolloClientInfo
        });
        try {
            const apiRecords = await pricingPromise;
            if (!apiRecords) return;
            const processedRecords = processRecords({
                apiRecords: apiRecords,
                doubleCalendarPricing: false
            });
            const numberOfProcessedRecords = Object.keys(processedRecords)?.length;
            if (numberOfProcessedRecords > 0 && !clearDayMap) {
                const { displayedFromMonth, displayedToMonth } = displayMonths;
                let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date());
                const toMonth = parse(displayedToMonth, 'yyyyMM', new Date());
                while(fromMonth.valueOf() <= toMonth.valueOf()){
                    executedMonths.current[format(fromMonth, 'yyyyMM')] = true;
                    fromMonth = addMonths(fromMonth, 1);
                }
            }
            setDayMap((prev)=>{
                const mergedData = clearDayMap ? processedRecords : {
                    ...prev.data ?? {},
                    ...processedRecords
                };
                const hasData = Object.keys(mergedData).length > 0;
                const lowestPrice = hasData ? getLowestPrice(mergedData) : void 0;
                return {
                    data: mergedData,
                    variant: DAY_MAP_VARIANT,
                    lowestPrice
                };
            });
        } catch  {
            const { displayedFromMonth, displayedToMonth } = displayMonths;
            let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date());
            const toMonth = parse(displayedToMonth, 'yyyyMM', new Date());
            while(fromMonth.valueOf() <= toMonth.valueOf()){
                const fromMonthKey = format(fromMonth, 'yyyyMM');
                delete executedMonths.current[fromMonthKey];
                fromMonth = addMonths(fromMonth, 1);
            }
        }
    }, [
        flightDetails,
        variantName,
        getGraphURL,
        apolloClientInfo
    ]);
    const handleCalendarDatesChange = useCallback((dates, triggerDate)=>{
        if (!dates) return;
        let displayedFromMonth;
        let displayedToMonth;
        let departDate;
        if ('range' === mode && 'from' in dates && dates.from) {
            displayedFromMonth = format(dates.from, 'yyyyMM');
            displayedToMonth = format(dates?.to || dates.from, 'yyyyMM');
            departDate = format(dates.from, 'yyyyMMdd');
        } else {
            displayedFromMonth = format(dates, 'yyyyMM');
            displayedToMonth = displayedFromMonth;
        }
        const displayMonths = {
            displayedFromMonth,
            displayedToMonth
        };
        getAirPricedCalendarData({
            displayMonths,
            departDate,
            clearDayMap: true
        });
        handleCalendarChange?.(dates, triggerDate);
    }, [
        getAirPricedCalendarData,
        handleCalendarChange,
        mode
    ]);
    const onMonthChange = useCallback((month)=>{
        const displayMonths = getDisplayMonths({
            startDate: month,
            numberOfMonths: PRICE_MONTHS
        });
        getAirPricedCalendarData({
            displayMonths,
            departDate: void 0
        });
    }, [
        getAirPricedCalendarData
    ]);
    useEffect(()=>{
        const displayMonths = getDisplayMonths({
            startDate: startDate ? new Date(startDate) : new Date(),
            numberOfMonths: PRICE_MONTHS
        });
        getAirPricedCalendarData({
            displayMonths
        });
    }, [
        getAirPricedCalendarData,
        startDate
    ]);
    const footerLeftNode = useMemo(()=>/*#__PURE__*/ jsx(FooterLeftNode, {
            lowestPrice: dayMap?.lowestPrice ?? 0
        }), [
        dayMap?.lowestPrice
    ]);
    return /*#__PURE__*/ jsx(DatePicker, {
        ...restProps,
        title: title,
        label: label,
        mode: mode,
        selected: selected,
        defaultMonth: defaultMonth,
        startMonth: startMonth,
        endMonth: endMonth,
        dayMap: dayMap,
        numberOfMonths: numberOfMonths,
        handleCalendarChange: handleCalendarDatesChange,
        onMonthChange: onMonthChange,
        footerLeftNode: footerLeftNode
    });
}
export { HorizonFlyDatePicker };
